import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface CategoryRatingProps {
    category: 'online' | 'management' | 'gameplay';
    rating: number;
    size?: 'sm' | 'md';
    showLabel?: boolean;
}

const CategoryRating: React.FC<CategoryRatingProps> = ({
    category,
    rating,
    size = 'sm',
    showLabel = true,
}) => {
    const { t } = useLanguage();

    const labels: Record<string, string> = {
        online: t('server.rating.online') || 'Online',
        management: t('server.rating.management') || 'Yönetim',
        gameplay: t('server.rating.gameplay') || 'Oyun',
    };

    const icons: Record<string, JSX.Element> = {
        online: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
            </svg>
        ),
        management: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
        ),
        gameplay: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    };

    const sizeClasses = {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
    };

    // If rating is 0, don't show anything
    if (rating === 0) {
        return null;
    }

    return (
        <div className="flex items-center gap-1.5">
            {showLabel && (
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[70px]">
                    <span className="text-blue-500 dark:text-blue-400">{icons[category]}</span>
                    <span className="text-xs font-medium">{labels[category]}:</span>
                </div>
            )}
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                        key={star}
                        className={`${sizeClasses[size]} transition-colors ${star <= rating
                                ? 'text-yellow-400'
                                : 'text-gray-300 dark:text-gray-600'
                            }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                ))}
            </div>
        </div>
    );
};

export default CategoryRating;
